"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createLogger = void 0;
const fast_printf_1 = require("fast-printf");
const globalthis_1 = __importDefault(require("globalthis"));
const config_1 = require("../config");
const constants_1 = require("../constants");
const utilities_1 = require("../utilities");
let loggedWarningAsyncLocalContext = false;
const globalThis = (0, globalthis_1.default)();
const getGlobalRoarrContext = () => {
    return globalThis.ROARR;
};
const createDefaultAsyncLocalContext = () => {
    return {
        messageContext: {},
        transforms: [],
    };
};
const getAsyncLocalContext = () => {
    const asyncLocalStorage = getGlobalRoarrContext().asyncLocalStorage;
    if (!asyncLocalStorage) {
        throw new Error('AsyncLocalContext is unavailable.');
    }
    const asyncLocalContext = asyncLocalStorage.getStore();
    if (asyncLocalContext) {
        return asyncLocalContext;
    }
    return createDefaultAsyncLocalContext();
};
const isAsyncLocalContextAvailable = () => {
    return Boolean(getGlobalRoarrContext().asyncLocalStorage);
};
const getSequence = () => {
    if (isAsyncLocalContextAvailable()) {
        const asyncLocalContext = getAsyncLocalContext();
        if ((0, utilities_1.hasOwnProperty)(asyncLocalContext, 'sequenceRoot') && (0, utilities_1.hasOwnProperty)(asyncLocalContext, 'sequence')) {
            return String(asyncLocalContext.sequenceRoot) + '.' + String(asyncLocalContext.sequence++);
        }
        return String(getGlobalRoarrContext().sequence++);
    }
    return String(getGlobalRoarrContext().sequence++);
};
const createLogger = (onMessage, parentMessageContext = {}, transforms = []) => {
    const log = (a, b, c, d, e, f, g, h, i, j) => {
        const time = Date.now();
        const sequence = getSequence();
        let asyncLocalContext;
        if (isAsyncLocalContextAvailable()) {
            asyncLocalContext = getAsyncLocalContext();
        }
        else {
            asyncLocalContext = createDefaultAsyncLocalContext();
        }
        let context;
        let message;
        if (typeof a === 'string') {
            context = {
                ...asyncLocalContext.messageContext,
                ...parentMessageContext,
            };
        }
        else {
            context = {
                ...asyncLocalContext.messageContext,
                ...parentMessageContext,
                ...a,
            };
        }
        if (typeof a === 'string' && b === undefined) {
            message = a;
        }
        else if (typeof a === 'string') {
            message = (0, fast_printf_1.printf)(a, b, c, d, e, f, g, h, i, j);
        }
        else {
            let fallbackMessage = b;
            if (typeof b !== 'string') {
                if (b === undefined) {
                    fallbackMessage = '';
                }
                else {
                    throw new TypeError('Message must be a string. Received ' + typeof b + '.');
                }
            }
            message = (0, fast_printf_1.printf)(fallbackMessage, c, d, e, f, g, h, i, j);
        }
        let packet = {
            context,
            message,
            sequence,
            time,
            version: config_1.ROARR_LOG_FORMAT_VERSION,
        };
        for (const transform of [...asyncLocalContext.transforms, ...transforms]) {
            packet = transform(packet);
            if (typeof packet !== 'object' || packet === null) {
                throw new Error('Message transform function must return a message object.');
            }
        }
        onMessage(packet);
    };
    /**
     * Creates a child logger with the provided context.
     * If context is an object, then its properties are prepended to all descending logs.
     * If context is a function, then that function is used to process all descending logs.
     */
    log.child = (context) => {
        let asyncLocalContext;
        if (isAsyncLocalContextAvailable()) {
            asyncLocalContext = getAsyncLocalContext();
        }
        else {
            asyncLocalContext = createDefaultAsyncLocalContext();
        }
        if (typeof context === 'function') {
            return (0, exports.createLogger)(onMessage, {
                ...asyncLocalContext.messageContext,
                ...parentMessageContext,
                ...context,
            }, [
                context,
                ...transforms,
            ]);
        }
        return (0, exports.createLogger)(onMessage, {
            ...asyncLocalContext.messageContext,
            ...parentMessageContext,
            ...context,
        }, transforms);
    };
    log.getContext = () => {
        let asyncLocalContext;
        if (isAsyncLocalContextAvailable()) {
            asyncLocalContext = getAsyncLocalContext();
        }
        else {
            asyncLocalContext = createDefaultAsyncLocalContext();
        }
        return {
            ...asyncLocalContext.messageContext,
            ...parentMessageContext,
        };
    };
    log.adopt = async (routine, context) => {
        if (!isAsyncLocalContextAvailable()) {
            if (loggedWarningAsyncLocalContext === false) {
                loggedWarningAsyncLocalContext = true;
                onMessage({
                    context: {
                        logLevel: constants_1.logLevels.warn,
                        package: 'roarr',
                    },
                    message: 'async_hooks are unavailable; Roarr.adopt will not function as expected',
                    sequence: getSequence(),
                    time: Date.now(),
                    version: config_1.ROARR_LOG_FORMAT_VERSION,
                });
            }
            return routine();
        }
        const asyncLocalContext = getAsyncLocalContext();
        let sequenceRoot;
        if ((0, utilities_1.hasOwnProperty)(asyncLocalContext, 'sequenceRoot')) {
            sequenceRoot = asyncLocalContext.sequenceRoot + '.' + String(asyncLocalContext.sequence++);
        }
        else {
            sequenceRoot = String(getGlobalRoarrContext().sequence++);
        }
        let nextContext = {
            ...asyncLocalContext.messageContext,
        };
        const nextTransforms = [
            ...asyncLocalContext.transforms,
        ];
        if (typeof context === 'function') {
            nextTransforms.push(context);
        }
        else {
            nextContext = {
                ...nextContext,
                ...context,
            };
        }
        const asyncLocalStorage = getGlobalRoarrContext().asyncLocalStorage;
        if (!asyncLocalStorage) {
            throw new Error('Async local context unavailable.');
        }
        return asyncLocalStorage.run({
            messageContext: nextContext,
            sequence: 0,
            sequenceRoot,
            transforms: nextTransforms,
        }, () => {
            return routine();
        });
    };
    log.trace = (a, b, c, d, e, f, g, h, i, j) => {
        log.child({
            logLevel: constants_1.logLevels.trace,
        })(a, b, c, d, e, f, g, h, i, j);
    };
    log.debug = (a, b, c, d, e, f, g, h, i, j) => {
        log.child({
            logLevel: constants_1.logLevels.debug,
        })(a, b, c, d, e, f, g, h, i, j);
    };
    log.info = (a, b, c, d, e, f, g, h, i, j) => {
        log.child({
            logLevel: constants_1.logLevels.info,
        })(a, b, c, d, e, f, g, h, i, j);
    };
    log.warn = (a, b, c, d, e, f, g, h, i, j) => {
        log.child({
            logLevel: constants_1.logLevels.warn,
        })(a, b, c, d, e, f, g, h, i, j);
    };
    log.error = (a, b, c, d, e, f, g, h, i, j) => {
        log.child({
            logLevel: constants_1.logLevels.error,
        })(a, b, c, d, e, f, g, h, i, j);
    };
    log.fatal = (a, b, c, d, e, f, g, h, i, j) => {
        log.child({
            logLevel: constants_1.logLevels.fatal,
        })(a, b, c, d, e, f, g, h, i, j);
    };
    return log;
};
exports.createLogger = createLogger;
